/**
 * Email theme HTML builders.
 *
 * Flow: base HTML ({{THEME.*}} slots + Go {{.Var}}) → resolveSkinTokens(skin)
 * → logo tokens → applyThemeToTemplate → final HTML for Content / preview.
 *
 * Templates load on demand (one chunk per handler) and stay cached.
 */

import {
    HANDLER_CONTACT_BUSINESS,
    HANDLER_ORDER_COMPLETED,
    HANDLER_ORDER_CONFIRM,
    HANDLER_ORDER_DECLINED,
    HANDLER_ORDER_ON_ROUTE,
    HANDLER_ORDER_READY,
} from "../constants/skins";
import { resolveLogoTokens } from "./logoTokens";
import { attachStatusFigureTokens } from "./statusFigures";
import { prepareEmailHtmlForAdminPreview } from "./prepareEmailHtmlForAdminPreview";
import {
    MOCK_PRISMIC_THEME,
    applyThemeToTemplate,
    assertEmailHtmlSafeToSave,
    expandThemeTokenPairs,
    resolveSkinTokens,
} from "./themeTokens";

/** Dynamic importers — keeps ~1MB of HTML out of the main admin chunk. */
const TEMPLATE_IMPORTERS = {
    [HANDLER_ORDER_CONFIRM]: () =>
        import(
            /* webpackChunkName: "email-theme-confirm" */ "../templates/order-confirm-notify.html"
        ),
    [HANDLER_ORDER_READY]: () =>
        import(
            /* webpackChunkName: "email-theme-ready" */ "../templates/order-ready-notify.html"
        ),
    [HANDLER_ORDER_ON_ROUTE]: () =>
        import(
            /* webpackChunkName: "email-theme-on-route" */ "../templates/order-on-route-notify.html"
        ),
    [HANDLER_ORDER_COMPLETED]: () =>
        import(
            /* webpackChunkName: "email-theme-completed" */ "../templates/order-completed-notify.html"
        ),
    [HANDLER_ORDER_DECLINED]: () =>
        import(
            /* webpackChunkName: "email-theme-declined" */ "../templates/order-declined-notify.html"
        ),
    [HANDLER_CONTACT_BUSINESS]: () =>
        import(
            /* webpackChunkName: "email-theme-contact" */ "../templates/contact-business-owner.html"
        ),
};

/** @type {Record<string, string>} */
const baseHtmlCache = Object.create(null);

/** @type {Record<string, Promise<string>>} */
const baseHtmlPending = Object.create(null);

function resolveImporterHandler(handler) {
    return Object.prototype.hasOwnProperty.call(TEMPLATE_IMPORTERS, handler)
        ? handler
        : HANDLER_ORDER_CONFIRM;
}

function htmlFromImportModule(mod) {
    if (typeof mod === "string") return mod;
    if (mod && typeof mod.default === "string") return mod.default;
    return "";
}

export function hasEmailThemeTemplate(handler) {
    return Object.prototype.hasOwnProperty.call(TEMPLATE_IMPORTERS, handler);
}

/**
 * Load (and cache) base HTML for a handler. Safe to call repeatedly.
 * @param {string} [handler]
 * @returns {Promise<string>}
 */
export function ensureBaseEmailHtml(handler = HANDLER_ORDER_CONFIRM) {
    const key = resolveImporterHandler(handler);
    if (baseHtmlCache[key]) {
        return Promise.resolve(baseHtmlCache[key]);
    }
    if (!baseHtmlPending[key]) {
        baseHtmlPending[key] = TEMPLATE_IMPORTERS[key]()
            .then(mod => {
                const html = htmlFromImportModule(mod);
                baseHtmlCache[key] = html;
                delete baseHtmlPending[key];
                return html;
            })
            .catch(err => {
                delete baseHtmlPending[key];
                throw err;
            });
    }
    return baseHtmlPending[key];
}

/** Sync read from cache (empty string until `ensureBaseEmailHtml` resolves). */
export function getBaseEmailHtml(handler = HANDLER_ORDER_CONFIRM) {
    const key = resolveImporterHandler(handler);
    return baseHtmlCache[key] || "";
}

/** Unique `{{THEME.tokenName}}` keys present in HTML. */
export function collectThemeTokenKeysFromHtml(html) {
    const keys = new Set();
    if (!html) return keys;
    const re = /\{\{THEME\.([A-Za-z0-9_]+)\}\}/g;
    let match;
    while ((match = re.exec(html)) !== null) {
        keys.add(match[1]);
    }
    return keys;
}

/** Token keys for handler (from cache; empty until template is loaded). */
export function getThemeTokenKeysForHandler(handler) {
    return collectThemeTokenKeysFromHtml(getBaseEmailHtml(handler));
}

/**
 * Whether a Customize Brand field applies to the template token set.
 * Includes Solid/Fill pairs and colors that only bake into generated icons.
 */
export function isCustomizeFieldUsedInTokens(fieldKey, tokenKeys) {
    if (!fieldKey || !tokenKeys || tokenKeys.size === 0) return true;
    if (tokenKeys.has(fieldKey)) return true;
    if (tokenKeys.has(`${fieldKey}Solid`)) return true;
    if (tokenKeys.has(`${fieldKey}Fill`)) return true;

    // Bag palette → baked into {{THEME.iconBag}}
    if (fieldKey.startsWith("bag") && tokenKeys.has("iconBag")) {
        return true;
    }

    // Decline art → baked into {{THEME.iconDeclined}} (declined email only)
    if (fieldKey.startsWith("declined") && tokenKeys.has("iconDeclined")) {
        return true;
    }

    // Status icon ink → baked into tracker step icons
    if (
        (fieldKey === "statusIconActive" ||
            fieldKey === "statusIconInactive") &&
        [...tokenKeys].some(t =>
            /^(iconPlaced|iconReady|iconOnRoute|iconCompleted)/.test(t),
        )
    ) {
        return true;
    }

    // Dot colors → baked into {{THEME.indicator*}}
    if (
        (fieldKey === "dotActive" || fieldKey === "dotInactive") &&
        [...tokenKeys].some(t => t.startsWith("indicator"))
    ) {
        return true;
    }

    return false;
}

/**
 * Color + logo tokens for a skin (no icon assets yet).
 * @param {string} skinId
 * @param {object} [options]
 */
export function buildEmailThemeColorTokens(
    skinId,
    {
        prismicTheme = MOCK_PRISMIC_THEME,
        tokenOverrides = null,
        logoSettings = null,
        forPreview = false,
    } = {},
) {
    return expandThemeTokenPairs({
        ...resolveSkinTokens(skinId, prismicTheme),
        ...(tokenOverrides && typeof tokenOverrides === "object"
            ? tokenOverrides
            : null),
        ...resolveLogoTokens(logoSettings, { forPreview }),
    });
}

/**
 * Fill the handler's base template with tokens for the selected skin.
 * Requires `ensureBaseEmailHtml(handler)` first (modal primes on open).
 * Preserves Go placeholders ({{.StoreLogo}}, {{ if }}, etc.) when logo URL is empty
 * and forPreview is false.
 *
 * @param {string} skinId
 * @param {object} [options]
 * @param {string} [options.handler]
 * @param {object} [options.prismicTheme]
 * @param {Record<string, string>} [options.tokenOverrides] CSS values merged on top of skin tokens
 * @param {object} [options.logoSettings] header/footer URL + dimensions
 * @param {boolean} [options.forPreview] use product placeholder when logo URL empty
 * @param {Record<string, string>} [options.iconTokens] CDN URLs (Apply); omit for data-URI preview
 * @param {object} [options.store] live store config for preview identity fields
 */
export function buildThemedEmailHtml(
    skinId,
    {
        handler = HANDLER_ORDER_CONFIRM,
        prismicTheme = MOCK_PRISMIC_THEME,
        tokenOverrides = null,
        logoSettings = null,
        forPreview = false,
        iconTokens = null,
        store = null,
    } = {},
) {
    const baseHtml = getBaseEmailHtml(handler);
    if (!baseHtml) return "";

    const colorTokens = buildEmailThemeColorTokens(skinId, {
        prismicTheme,
        tokenOverrides,
        logoSettings,
        forPreview,
    });
    const iconKeys = collectThemeTokenKeysFromHtml(baseHtml);
    // Figure tokens (data URIs) only for icons this template references.
    const withFigures = attachStatusFigureTokens(colorTokens, iconKeys);
    const tokens =
        iconTokens && typeof iconTokens === "object"
            ? { ...withFigures, ...iconTokens }
            : withFigures;
    const themed = applyThemeToTemplate(baseHtml, tokens);
    if (forPreview) {
        // Sample order/contact data for the iframe only — never saved on Apply.
        return prepareEmailHtmlForAdminPreview(themed, {
            orderType: "DELIVERY",
            store,
        });
    }
    // Never persist HTML that still contains {{THEME.*}} (breaks Go send).
    return assertEmailHtmlSafeToSave(themed);
}
